import { useState, useEffect, useMemo, useCallback } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import useFetchData from '../../../../hooks/UseFetchData'
import { payersOptionsFormat } from '../../../../constants/BeneficiaryConstants'
import {
  addPolicyReportJob,
  updatePolicyReportJob,
  removePolicyReportJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getOneMonthMaxDate, getMinDate } from '../../../../utils/dateRangeValidation'

const PolicyReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.policyReportJobs || [])
  const loggedUser = useSelector(state => state.auth.user)
  const shouldSkipItemCategory = Number(loggedUser?.business_id) == 27

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'policy_report_async_new',
    {
      start_date: moment().subtract(1, 'day').toDate(),
    end_date: moment().toDate(),
    payer_id: '',
    invoice_number: '',
    category: 'insurance',
    format: 'pdf'
    }
  )

  const [payers, setPayers] = useState([])
  const { executeFetch: fetchPayers } = useFetchData()

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addPolicyReportJob,
    updateJobAction: updatePolicyReportJob,
    removeJobAction: removePolicyReportJob,
    createEndpoint: '/v3/reports/payer-policy/jobs',
    statusEndpoint: '/v3/reports/payer-policy/jobs',
    downloadEndpoint: '/v3/reports/payer-policy/jobs',
    fileNamePrefix: 'insurance_payer_policy_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      payer_id: filters.payer_id || undefined,
      invoice_number: filters.invoice_number || undefined,
      ...(shouldSkipItemCategory
        ? { skip_item_category: true }
        : { item_category: filters.category || "insurance" }),
      format
    })
  })

  const loadPayers = useCallback(async () => {
    const res = await fetchPayers('POST', '/v3/payer/get', {})
    if (res?.data) {
      setPayers(res?.data)
    }
  }, [fetchPayers])

  useEffect(() => {
    loadPayers()
  }, [loadPayers])

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => {
    const { name, value } = e.target
    
    if (name === 'payer_id' && value) {
      const selectedPayer = payers.find(payer => payer.payer_id === value)
      const payerCategory = selectedPayer?.category || 'insurance'
      
      setFilters(prev => ({ 
        ...prev, 
        [name]: value,
        category: payerCategory
      }))
    } else if (name === 'payer_id' && !value) {
      setFilters(prev => ({ 
        ...prev, 
        [name]: value,
        category: 'insurance'
      }))
    } else {
      setFilters(prev => ({ ...prev, [name]: value }))
    }
  }

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'payer_id',
      label: 'Payer',
      options: payersOptionsFormat(payers),
      placeholder: 'All Payers',
      filter: true,
      width: 'w-[200px]'
    },
    {
      type: 'text',
      name: 'invoice_number',
      label: 'Invoice Number',
      placeholder: 'Invoice Number',
      width: 'w-[160px]'
    },
    ...(!shouldSkipItemCategory ? [{
      type: 'dropdown',
      name: 'category',
      label: 'Category',
      options: [
      //  { label: t('All'), value: '' },
        { label: t('Insurance'), value: 'insurance' },
        { label: t('Corporate'), value: 'corporate' },
        { label: t('Government'), value: 'government' }
      ],
      placeholder: '',
      width: 'w-[160px]'
    }] : []),
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [payers, shouldSkipItemCategory, t, filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('PolicyReport')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('PolicyReport')}
      />
    </div>
  )
}

export default PolicyReportAsync
